import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { errMsg } from "../lib/api";
import { Btn, inputCls } from "../components/common";
import { fmtIDR, fmtDate } from "../lib/format";
import { LogOut, Home, Wallet, Hammer, CheckCircle2, Clock, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";

function LogPhoto({ id }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/progress/${id}/photo`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data);
      setSrc(url);
    }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id]);
  if (!src) return null;
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <img src={src} alt="Foto progress pembangunan" className="mt-2 w-full h-36 object-cover rounded-lg border border-slate-800" data-testid={`portal-log-photo-${id}`} />
    </a>
  );
}

export default function PortalKonsumen() {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [komplain, setKomplain] = useState("");
  const [complaints, setComplaints] = useState([]);
  const [diri, setDiri] = useState(null);
  const [savingDiri, setSavingDiri] = useState(false);

  const viewBerkas = async (k) => {
    try {
      const r = await api.get(`/portal/berkas/${k}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveDiri = async () => {
    setSavingDiri(true);
    try {
      await api.put("/portal/profile", diri);
      toast.success("Data diri berhasil disimpan");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingDiri(false); }
  };

  const fetchComplaints = () => api.get("/portal/complaints").then((r) => setComplaints(r.data)).catch(() => {});

  const kirimKomplain = async () => {
    try {
      const r = await api.post("/portal/complaints", { message: komplain });
      toast.success(r.data.message);
      setKomplain("");
      fetchComplaints();
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    api.get("/portal/me").then((r) => { setData(r.data); if (r.data.form) setDiri({ ...r.data.form }); }).catch((e) => setErr(errMsg(e)));
    fetchComplaints();
  }, []);

  if (err) return <div className="min-h-screen flex items-center justify-center text-rose-400 p-6" data-testid="portal-error">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="portal-loading">Memuat data pemesanan...</div>;

  const hargaTotal = Math.max((data.price || 0) - (data.discount || 0) + (data.addon_total || 0), 0);
  const totalTagihan = data.booking_fee + (data.schedules || []).reduce((s, x) => s + (x.amount || 0), 0);
  const totalTerbayar = (data.booking_fee_paid || 0) + (data.schedules || []).reduce((s, x) => s + (x.paid_amount || 0), 0);
  const pctBayar = totalTagihan > 0 ? Math.min(Math.round((totalTerbayar / totalTagihan) * 100), 100) : 0;
  const stepStatus = { booking: 1, dp: 2, proses_bank: 3, closing: 4, serah_terima: 5 };
  const currentStep = data.status === "lunas" ? 5 : data.tanggal_akad ? 4 : data.booking_fee_paid >= (data.booking_fee || 0) && (data.booking_fee || 0) > 0 ? 3 : 2;

  return (
    <div className="min-h-screen pb-10" data-testid="portal-konsumen">
      <header className="bg-white border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-2.5">
            <img src="/icons/icon-192.png" alt="SPASI" className="w-9 h-9 rounded-lg" />
            <div>
              <div className="font-extrabold text-sm tracking-[0.2em] text-slate-200">SPASI</div>
              <div className="text-[10px] font-medium text-emerald-600">Portal Konsumen</div>
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-rose-400 transition-colors" data-testid="portal-logout">
            <LogOut className="w-4 h-4" /> Keluar
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-6 space-y-4">
        <div className="bg-white border border-slate-800 rounded-2xl p-5">
          <p className="text-xs text-slate-500">Selamat datang,</p>
          <h1 className="text-xl font-bold text-slate-200" data-testid="portal-name">{data.customer_name}</h1>
          <div className="mt-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-950 flex items-center justify-center shrink-0"><Home className="w-5 h-5 text-emerald-500" /></div>
            <div className="flex-1">
              <div className="font-semibold text-slate-200" data-testid="portal-unit">Unit {data.unit?.label} — {data.unit?.type}</div>
              <div className="text-xs text-slate-500">{data.location || "-"}</div>
              <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-600" data-testid="portal-unit-status">
                {data.unit?.under_construction ? "Booking + Sedang Dibangun" : (data.unit?.status || "-").toUpperCase()}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-slate-500 uppercase">Harga Akhir</div>
              <div className="font-mono font-bold text-emerald-600" data-testid="portal-price">{fmtIDR(hargaTotal)}</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-800 rounded-2xl p-5" data-testid="portal-steps">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Tahapan Pemesanan Rumah</div>
          {(() => {
            const WF = [["booking", "Booking & Tanda Jadi"], ["berkas", "Kelengkapan Berkas"], ["ke_bank", "Berkas ke Bank KPR"], ["approved_bank", "Di-approve Bank"], ["spk", "SPK Pembangunan Terbit"], ["akad_notice", "Pemberitahuan Akad"], ["dp_lunas", "Pelunasan DP"], ["akad", "Akad"], ["serah_terima", "Serah Terima Kunci"], ["selesai", "Selesai"]];
            const cur = WF.findIndex(([k]) => k === (data.process_stage || "booking"));
            const logMap = {};
            (data.process_log || []).forEach((l) => { logMap[l.stage] = l.at; });
            return WF.map(([k, label], i) => {
              const done = i < cur || data.process_stage === "selesai";
              const active = i === cur && data.process_stage !== "selesai";
              return (
                <div key={k} className="flex items-center gap-3 py-1.5" data-testid={`portal-step-${k}`}>
                  {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> : <Clock className={`w-5 h-5 shrink-0 ${active ? "text-emerald-600" : "text-slate-300"}`} />}
                  <span className={`text-sm flex-1 ${done ? "text-slate-400" : active ? "text-slate-200 font-semibold" : "text-slate-500"}`}>{label}</span>
                  {logMap[k] && <span className="text-[10px] font-mono text-slate-500">{fmtDate(logMap[k].slice(0, 10))}</span>}
                </div>
              );
            });
          })()}
          {data.tanggal_akad && <p className="text-xs text-slate-500 mt-2">Akad: {fmtDate(data.tanggal_akad)} {data.bank_akad ? `di ${data.bank_akad}` : ""}</p>}
        </div>

        <div className="bg-white border border-slate-800 rounded-2xl p-5" data-testid="portal-berkas">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Kelengkapan Berkas</div>
          {[["ktp_pemohon", "KTP Pemohon"], ...(data.menikah ? [["ktp_pasangan", "KTP Pasangan"]] : []), ["kk", "Kartu Keluarga"], ["npwp", "NPWP"]].map(([k, label]) => {
            const up = data.berkas?.[k]?.uploaded;
            return (
              <div key={k} className="flex items-center gap-3 py-1.5" data-testid={`portal-berkas-${k}`}>
                {up ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Clock className="w-4 h-4 text-slate-300 shrink-0" />}
                {up ? (
                  <button onClick={() => viewBerkas(k)} className="text-sm text-emerald-600 hover:text-emerald-500 underline underline-offset-2 text-left" data-testid={`portal-berkas-view-${k}`}>{label}</button>
                ) : (
                  <span className="text-sm text-slate-500">{label}</span>
                )}
                {up && <span className="text-[10px] font-mono text-slate-500 ml-auto">{fmtDate(data.berkas[k].at?.slice(0, 10))}</span>}
              </div>
            );
          })}
          <p className="text-[11px] text-slate-500 mt-2">Berkas diunggah oleh Sales — klik nama berkas untuk melihat detailnya.</p>
        </div>

        {diri && (
          <div className="bg-white border border-slate-800 rounded-2xl p-5" data-testid="portal-data-diri">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Data Diri Saya (sesuai Formulir Booking)</div>
            <div className="space-y-3">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 block mb-1">NIK (KTP)</span>
                <input inputMode="numeric" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/60" value={diri.nik || ""} onChange={(e) => setDiri({ ...diri, nik: e.target.value })} data-testid="portal-diri-nik" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 block mb-1">Tempat Lahir</span>
                  <input className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/60" value={diri.tempat_lahir || ""} onChange={(e) => setDiri({ ...diri, tempat_lahir: e.target.value })} data-testid="portal-diri-tempat" />
                </div>
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 block mb-1">Tanggal Lahir</span>
                  <input type="date" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/60" value={diri.tanggal_lahir || ""} onChange={(e) => setDiri({ ...diri, tanggal_lahir: e.target.value })} data-testid="portal-diri-tanggal" />
                </div>
              </div>
              <div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 block mb-1">Alamat</span>
                <textarea rows={2} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/60" value={diri.alamat || ""} onChange={(e) => setDiri({ ...diri, alamat: e.target.value })} data-testid="portal-diri-alamat" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 block mb-1">No. HP</span>
                  <input className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/60" value={diri.no_hp || ""} onChange={(e) => setDiri({ ...diri, no_hp: e.target.value })} data-testid="portal-diri-hp" />
                </div>
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 block mb-1">Pekerjaan</span>
                  <input className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/60" value={diri.pekerjaan || ""} onChange={(e) => setDiri({ ...diri, pekerjaan: e.target.value })} data-testid="portal-diri-pekerjaan" />
                </div>
              </div>
              <button onClick={saveDiri} disabled={savingDiri} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg py-2.5 transition-colors disabled:opacity-50" data-testid="portal-diri-save">
                {savingDiri ? "Menyimpan..." : "Simpan Data Diri"}
              </button>
            </div>
          </div>
        )}

        {data.komplain_deadline && new Date() < new Date(data.komplain_deadline) && (
          <div className="bg-white border border-slate-800 rounded-2xl p-5" data-testid="portal-komplain">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Komplain (maks 90 hari setelah serah terima)</div>
            <p className="text-[11px] text-amber-600 mb-2">Batas komplain: {fmtDate(data.komplain_deadline.slice(0, 10))}</p>
            <textarea className={inputCls} rows={2} placeholder="Tulis komplain Anda..." value={komplain} onChange={(e) => setKomplain(e.target.value)} data-testid="portal-komplain-input" />
            <div className="flex justify-end mt-2"><Btn onClick={kirimKomplain} disabled={!komplain.trim()} data-testid="portal-komplain-send">Kirim Komplain</Btn></div>
          </div>
        )}

        <div className="bg-white border border-slate-800 rounded-2xl p-5" data-testid="portal-payment">
          <div className="flex items-center gap-2 mb-3"><Wallet className="w-4 h-4 text-emerald-500" /><span className="text-xs font-mono uppercase tracking-wider text-slate-500">Progress Pembayaran</span></div>
          <div className="flex justify-between text-sm mb-1.5">
            <span className="text-slate-400">Terbayar</span>
            <span className="font-mono text-slate-200" data-testid="portal-paid">{fmtIDR(totalTerbayar)} / {fmtIDR(totalTagihan)} ({pctBayar}%)</span>
          </div>
          <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pctBayar}%` }} data-testid="portal-paybar" />
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-xs py-1.5 border-b border-slate-800/60">
              <span className="text-slate-400">Booking Fee ({fmtIDR(data.booking_fee || 0)})</span>
              <span className={`font-medium ${data.booking_fee_paid >= (data.booking_fee || 0) ? "text-emerald-600" : "text-amber-500"}`}>
                {data.booking_fee_paid >= (data.booking_fee || 0) ? "LUNAS" : `Terbayar ${fmtIDR(data.booking_fee_paid || 0)}`}
              </span>
            </div>
            {(data.schedules || []).map((s, i) => (
              <div key={i} className="flex justify-between text-xs py-1.5 border-b border-slate-800/60 last:border-0" data-testid={`portal-schedule-${i}`}>
                <span className="text-slate-400">{s.label}{s.due_date ? ` — jt. tempo ${fmtDate(s.due_date)}` : ""}</span>
                <span className={`font-medium ${s.status === "lunas" ? "text-emerald-600" : "text-slate-300"}`}>
                  {s.status === "lunas" ? "LUNAS" : `${fmtIDR(s.paid_amount || 0)} / ${fmtIDR(s.amount)}`}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-800 rounded-2xl p-5" data-testid="portal-construction">
          <div className="flex items-center gap-2 mb-3"><Hammer className="w-4 h-4 text-emerald-500" /><span className="text-xs font-mono uppercase tracking-wider text-slate-500">Progress Pembangunan</span></div>
          {!data.project ? (
            <div className="flex items-center gap-2.5 text-sm text-slate-500" data-testid="portal-construction-not-started">
              <Clock className="w-4 h-4 text-slate-300 shrink-0" /> Belum Dimulai — pembangunan akan dimulai setelah SPK terbit.
            </div>
          ) : (
            <>
            <div className="flex justify-between text-sm mb-1.5">
              <span className="text-slate-400">{data.project.name}</span>
              <span className="font-mono font-bold text-emerald-600" data-testid="portal-progress">{data.project.progress}%</span>
            </div>
            <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${data.project.progress}%` }} />
            </div>
            {data.progress_logs?.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Update Terverifikasi Terbaru</div>
                {data.progress_logs.map((l) => (
                  <div key={l.id} className="text-xs bg-slate-950 border border-slate-800 rounded-lg px-3 py-2" data-testid={`portal-log-${l.id}`}>
                    <span className="text-slate-300">{l.note || l.description || "Update progress"}</span>
                    <span className="text-slate-500 block mt-0.5">{fmtDate((l.created_at || "").slice(0, 10))} {l.percent != null ? `— ${l.percent}%` : ""}</span>
                    {l.has_photo && <LogPhoto id={l.id} />}
                  </div>
                ))}
              </div>
            )}
            </>
          )}
        </div>

        {complaints.length > 0 && (
          <div className="bg-white border border-slate-800 rounded-2xl p-5" data-testid="portal-complaints-history">
            <div className="flex items-center gap-2 mb-3"><MessageSquareWarning className="w-4 h-4 text-emerald-500" /><span className="text-xs font-mono uppercase tracking-wider text-slate-500">Riwayat Komplain</span></div>
            {complaints.map((c) => (
              <div key={c.id} className="flex items-start gap-3 py-2 border-b border-slate-800/60 last:border-0" data-testid={`portal-complaint-${c.id}`}>
                <div className="flex-1">
                  <p className="text-sm text-slate-300">{c.message}</p>
                  <p className="text-[10px] font-mono text-slate-500 mt-0.5">{fmtDate((c.created_at || "").slice(0, 10))}</p>
                </div>
                <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border shrink-0 ${c.status === "selesai" ? "border-emerald-500/40 text-emerald-600" : c.status === "diproses" ? "border-amber-500/40 text-amber-600" : "border-slate-500/40 text-slate-400"}`} data-testid={`portal-complaint-status-${c.id}`}>
                  {c.status === "selesai" ? "Selesai" : c.status === "diproses" ? "Diproses" : "Baru"}
                </span>
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-[11px] text-slate-500 pt-2">SPASI — Satu Spasi, Semua Terkoneksi · PT Abad Jaya Wampu</p>
      </main>
    </div>
  );
}
